import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { TrendingUp, TrendingDown, Search, X, CirclePlus, Plus, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { fetchStockMetadata } from '../../utils/stockMetadataBlob';
import { formatCompanyName } from '../../utils/formatters';

const GreenPupilEye = ({ className, isActive = true }: { className?: string, isActive?: boolean }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
    <circle 
      cx="12" cy="12" r="3" 
      fill={isActive ? "#22c55e" : "none"} 
      stroke={isActive ? "#22c55e" : "currentColor"} 
    />
  </svg>
);

interface StockData {
  symbol: string;
  name: string;
  price: number;
  change: number;
  changePercent: number;
  lastUpdate?: string;
}

interface WatchlistProps {
  selectedStock: string;
  onStockSelect: (symbol: string) => void;
  showFavoritesOnly?: boolean;
}

interface WatchlistGroup {
  id: string;
  name: string;
  symbols: string[];
}

interface WatchlistGroupsState {
  lists: WatchlistGroup[];
  activeId: string;
}

const MAX_FAVORITES = 20;
const MAX_LISTS = 10;
const MAX_LIST_NAME_LENGTH = 24;
const GROUPS_STORAGE_KEY = 'watchlist-groups';
// Single-list key from before named watchlists; still mirrors the active list because Dashboard reads it for the initial stock
const LEGACY_STORAGE_KEY = 'watchlist-favorites';
const DEFAULT_SYMBOLS = ['BBRI', 'BBCA', 'BMRI'];
const FALLBACK_LIST: WatchlistGroup = { id: 'main', name: 'Utama', symbols: [] };

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every(item => typeof item === 'string');

const isWatchlistGroup = (value: unknown): value is WatchlistGroup => {
  if (!value || typeof value !== 'object') return false;
  const group = value as Record<string, unknown>;
  return typeof group['id'] === 'string' && typeof group['name'] === 'string' && isStringArray(group['symbols']);
};

const createListId = () => `wl-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

function loadWatchlistGroups(): WatchlistGroupsState {
  try {
    const saved = localStorage.getItem(GROUPS_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved) as Record<string, unknown>;
      const lists = Array.isArray(parsed['lists']) ? parsed['lists'].filter(isWatchlistGroup) : [];
      const firstList = lists[0];
      if (firstList) {
        const activeId = lists.some(list => list.id === parsed['activeId']) ? String(parsed['activeId']) : firstList.id;
        return { lists, activeId };
      }
    }
  } catch {
    // Corrupt data: fall back to migrating the legacy single list
  }

  let symbols = DEFAULT_SYMBOLS;
  try {
    const legacy = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (legacy) {
      const parsed: unknown = JSON.parse(legacy);
      if (isStringArray(parsed)) symbols = parsed;
    }
  } catch {
    // ignore
  }
  return { lists: [{ ...FALLBACK_LIST, symbols }], activeId: FALLBACK_LIST.id };
}

export function Watchlist({ selectedStock, onStockSelect }: WatchlistProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [groups, setGroups] = useState<WatchlistGroupsState>(loadWatchlistGroups);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [stocksData, setStocksData] = useState<StockData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; symbol: string } | null>(null);
  const [listMenu, setListMenu] = useState<{ x: number; y: number; id: string } | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  // id null = creating a new watchlist, otherwise renaming that one
  const [editingList, setEditingList] = useState<{ id: string | null; name: string } | null>(null);
  const cancelEditRef = useRef(false);

  const activeList = groups.lists.find(list => list.id === groups.activeId) ?? groups.lists[0] ?? FALLBACK_LIST;
  const favorites = activeList.symbols;

  const setFavorites = (update: (prev: string[]) => string[]) => {
    setGroups(prev => ({
      ...prev,
      lists: prev.lists.map(list => (list.id === prev.activeId ? { ...list, symbols: update(list.symbols) } : list)),
    }));
  };

  useEffect(() => {
    localStorage.setItem(GROUPS_STORAGE_KEY, JSON.stringify(groups));
    localStorage.setItem(LEGACY_STORAGE_KEY, JSON.stringify(favorites));
  }, [groups, favorites]);

  useEffect(() => {
    let isMounted = true;
    const loadSnapshot = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const rows = await fetchStockMetadata();
        const stocks: StockData[] = rows.map((stock) => {
          const item: StockData = {
            symbol: stock.symbol,
            name: stock.name || stock.symbol,
            price: stock.price,
            change: stock.change,
            changePercent: stock.changePercent,
          };
          if (stock.lastUpdate !== undefined) item.lastUpdate = stock.lastUpdate;
          return item;
        });
        if (isMounted) setStocksData(stocks);
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to load watchlist data');
          setStocksData([]);
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };
    loadSnapshot();
    return () => { isMounted = false; };
  }, []);

  // Close context menus on outside click
  useEffect(() => {
    const handleClick = () => {
      setContextMenu(null);
      setListMenu(null);
      setConfirmDeleteId(null);
    };
    window.addEventListener('click', handleClick);
    return () => window.removeEventListener('click', handleClick);
  }, []);

  const toggleFavorite = (symbol: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setFavorites(prev => {
      if (prev.includes(symbol)) return prev.filter(f => f !== symbol);
      if (prev.length >= MAX_FAVORITES) return prev; // max 20
      return [...prev, symbol];
    });
  };

  const removeFavorite = (symbol: string) => {
    setFavorites(prev => prev.filter(f => f !== symbol));
    setContextMenu(null);
  };

  const handleContextMenu = (e: React.MouseEvent, symbol: string) => {
    e.preventDefault();
    setListMenu(null);
    setContextMenu({ x: e.clientX, y: e.clientY, symbol });
  };

  const copyToList = (symbol: string, listId: string) => {
    setGroups(prev => ({
      ...prev,
      lists: prev.lists.map(list =>
        list.id === listId && !list.symbols.includes(symbol) && list.symbols.length < MAX_FAVORITES
          ? { ...list, symbols: [...list.symbols, symbol] }
          : list
      ),
    }));
    setContextMenu(null);
  };

  const selectList = (id: string) => {
    setGroups(prev => ({ ...prev, activeId: id }));
  };

  const openListMenu = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu(null);
    setConfirmDeleteId(null);
    setListMenu({ x: e.clientX, y: e.clientY, id });
  };

  const startCreateList = () => {
    cancelEditRef.current = false;
    setEditingList({ id: null, name: '' });
  };

  const startRenameList = (list: WatchlistGroup) => {
    cancelEditRef.current = false;
    setListMenu(null);
    setEditingList({ id: list.id, name: list.name });
  };

  // Adds " 2", " 3", ... when another watchlist already uses the name
  const uniqueListName = (name: string, excludeId: string | null) => {
    const taken = new Set(groups.lists.filter(list => list.id !== excludeId).map(list => list.name.toLowerCase()));
    if (!taken.has(name.toLowerCase())) return name;
    let n = 2;
    while (taken.has(`${name} ${n}`.toLowerCase())) n++;
    return `${name} ${n}`;
  };

  // Runs on blur only; Enter and Escape blur the input so a name is never committed twice
  const commitListName = () => {
    const editing = editingList;
    setEditingList(null);
    if (!editing || cancelEditRef.current) {
      cancelEditRef.current = false;
      return;
    }
    const name = editing.name.trim().slice(0, MAX_LIST_NAME_LENGTH);
    if (!name) return;
    const finalName = uniqueListName(name, editing.id);

    if (editing.id === null) {
      if (groups.lists.length >= MAX_LISTS) return;
      const newList: WatchlistGroup = { id: createListId(), name: finalName, symbols: [] };
      setGroups(prev => ({ lists: [...prev.lists, newList], activeId: newList.id }));
    } else {
      const id = editing.id;
      setGroups(prev => ({
        ...prev,
        lists: prev.lists.map(list => (list.id === id ? { ...list, name: finalName } : list)),
      }));
    }
  };

  const handleListNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') e.currentTarget.blur();
    if (e.key === 'Escape') {
      cancelEditRef.current = true;
      e.currentTarget.blur();
    }
  };

  const deleteList = (id: string) => {
    setGroups(prev => {
      if (prev.lists.length <= 1) return prev;
      const lists = prev.lists.filter(list => list.id !== id);
      const activeId = prev.activeId === id ? (lists[0]?.id ?? prev.activeId) : prev.activeId;
      return { lists, activeId };
    });
    setListMenu(null);
    setConfirmDeleteId(null);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    setShowSearchDropdown(value.length > 0);
  };

  const handleSearchBlur = () => {
    setTimeout(() => setShowSearchDropdown(false), 200);
  };

  const handleStockSelectFromSearch = (symbol: string) => {
    onStockSelect(symbol);
    setSearchQuery('');
    setShowSearchDropdown(false);
  };

  const normalizedSearchQuery = searchQuery.trim().toLowerCase();

  const searchResults = useMemo(() => {
    if (!normalizedSearchQuery) return [];
    
    const filtered = stocksData.filter(stock => 
      stock.symbol.toLowerCase().includes(normalizedSearchQuery) || 
      stock.name.toLowerCase().includes(normalizedSearchQuery)
    );

    return filtered.sort((a, b) => {
      const aSymbol = a.symbol.toLowerCase();
      const bSymbol = b.symbol.toLowerCase();
      
      const aExact = aSymbol === normalizedSearchQuery;
      const bExact = bSymbol === normalizedSearchQuery;
      if (aExact && !bExact) return -1;
      if (!aExact && bExact) return 1;

      const aStarts = aSymbol.startsWith(normalizedSearchQuery);
      const bStarts = bSymbol.startsWith(normalizedSearchQuery);
      if (aStarts && !bStarts) return -1;
      if (!aStarts && bStarts) return 1;

      const aIncludes = aSymbol.includes(normalizedSearchQuery);
      const bIncludes = bSymbol.includes(normalizedSearchQuery);
      if (aIncludes && !bIncludes) return -1;
      if (!aIncludes && bIncludes) return 1;

      return aSymbol.localeCompare(bSymbol);
    }).slice(0, 30);
  }, [normalizedSearchQuery, stocksData]);

  // Main list: only favorites, max 20, ordered by favorites array
  const favoriteStocks = useMemo(() => {
    return favorites
      .slice(0, MAX_FAVORITES)
      .map(sym => stocksData.find(s => s.symbol === sym))
      .filter(Boolean) as StockData[];
  }, [favorites, stocksData]);

  const selectedStockData = useMemo(() => {
    return stocksData.find(s => s.symbol === selectedStock);
  }, [stocksData, selectedStock]);

  const selectedStockName = selectedStockData ? formatCompanyName(selectedStockData.name) : formatCompanyName(selectedStock);

  const otherLists = groups.lists.filter(list => list.id !== activeList.id);
  const listMenuTarget = listMenu ? groups.lists.find(list => list.id === listMenu.id) : undefined;

  return (
    <Card className="h-full flex flex-col gap-0 overflow-hidden relative rounded border border-border">
      <CardHeader className="p-2 pb-1.5 px-[5%] z-10 bg-background">
        <CardTitle className="mb-1.5">
          <div className="flex items-center justify-between">
            <div className="flex items-baseline gap-1.5 overflow-hidden">
              <span className="text-sm font-bold truncate">{selectedStock}</span>
              {selectedStockData && (
                <>
                  <span className="text-sm font-bold font-mono ml-1">{selectedStockData.price.toLocaleString()}</span>
                  <span className={`text-[10px] font-semibold px-1 rounded ${
                    selectedStockData.change >= 0 ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'
                  }`}>
                    {selectedStockData.change >= 0 ? '+' : ''}{selectedStockData.change} ({selectedStockData.changePercent.toFixed(2)}%)
                  </span>
                </>
              )}
            </div>
            <div className="flex items-center gap-1 text-[11px] text-muted-foreground shrink-0 pl-1">
              <GreenPupilEye className="w-3.5 h-3.5 text-muted-foreground" />
              {favorites.length}/{MAX_FAVORITES}
            </div>
          </div>
        </CardTitle>

        <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide mb-1.5">
          {groups.lists.map(list => {
            if (editingList?.id === list.id) {
              return (
                <input
                  key={list.id}
                  autoFocus
                  value={editingList.name}
                  maxLength={MAX_LIST_NAME_LENGTH}
                  onChange={(e) => setEditingList({ id: list.id, name: e.target.value })}
                  onKeyDown={handleListNameKeyDown}
                  onBlur={commitListName}
                  aria-label="Watchlist name"
                  className="shrink-0 w-24 px-2 py-0.5 text-[11px] rounded-full border border-primary bg-background text-foreground focus:outline-none"
                />
              );
            }
            const isActive = list.id === activeList.id;
            return (
              <div
                key={list.id}
                className={`flex items-center shrink-0 rounded-full border text-[11px] transition-colors ${
                  isActive
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border text-muted-foreground hover:text-foreground hover:border-primary/50'
                }`}
              >
                <button
                  onClick={() => selectList(list.id)}
                  onDoubleClick={() => startRenameList(list)}
                  onContextMenu={(e) => openListMenu(e, list.id)}
                  className={`flex items-center gap-1 py-0.5 pl-2.5 ${isActive ? 'pr-1' : 'pr-2.5'}`}
                  title={list.name}
                >
                  <span className="max-w-[96px] truncate font-medium">{list.name}</span>
                  <span className="opacity-70">{list.symbols.length}</span>
                </button>
                {isActive && (
                  <button
                    onClick={(e) => openListMenu(e, list.id)}
                    className="py-0.5 pr-1.5 hover:text-foreground"
                    aria-label={`Manage ${list.name}`}
                    title="Rename or delete"
                  >
                    <MoreHorizontal className="w-3 h-3" />
                  </button>
                )}
              </div>
            );
          })}
          {editingList?.id === null ? (
            <input
              autoFocus
              value={editingList.name}
              maxLength={MAX_LIST_NAME_LENGTH}
              placeholder="Watchlist name"
              onChange={(e) => setEditingList({ id: null, name: e.target.value })}
              onKeyDown={handleListNameKeyDown}
              onBlur={commitListName}
              aria-label="New watchlist name"
              className="shrink-0 w-24 px-2 py-0.5 text-[11px] rounded-full border border-primary bg-background text-foreground placeholder:text-muted-foreground focus:outline-none"
            />
          ) : groups.lists.length < MAX_LISTS && (
            <button
              onClick={startCreateList}
              className="shrink-0 w-5 h-5 flex items-center justify-center rounded-full border border-dashed border-border text-muted-foreground hover:text-foreground hover:border-primary/50"
              aria-label="New watchlist"
              title="New watchlist"
            >
              <Plus className="w-3 h-3" />
            </button>
          )}
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
          <input
            type="text"
            placeholder="Search & add stocks..."
            value={searchQuery}
            onChange={handleSearchChange}
            onFocus={() => searchQuery.length > 0 && setShowSearchDropdown(true)}
            onBlur={handleSearchBlur}
            className="w-full pl-10 pr-8 py-2 border border-border rounded bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
          {searchQuery && (
            <button
              onClick={() => { setSearchQuery(''); setShowSearchDropdown(false); }}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="w-4 h-4" />
            </button>
          )}

          {showSearchDropdown && searchResults.length > 0 && (
            <div 
              className="absolute top-full left-0 right-0 mt-1 bg-background border border-border rounded-md shadow-lg z-50 max-h-60 overflow-y-auto"
              onMouseDown={(e) => e.preventDefault()}
            >
              {searchResults.map(stock => {
                const isFav = favorites.includes(stock.symbol);
                const changeClass = stock.change === 0 ? 'text-muted-foreground' : stock.change > 0 ? 'text-green-600' : 'text-red-600';
                const badgeVariant = stock.changePercent === 0 ? 'secondary' : stock.changePercent > 0 ? 'default' : 'destructive';
                return (
                  <div
                    key={stock.symbol}
                    className="flex items-center justify-between p-2.5 hover:bg-muted/50 cursor-pointer border-b border-border/50 last:border-b-0"
                    onClick={() => handleStockSelectFromSearch(stock.symbol)}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="font-medium text-[13px] text-card-foreground">{stock.symbol}</span>
                        <Badge variant={badgeVariant} className="text-[9px] px-1 py-0 h-3.5 flex items-center gap-0.5">
                          {stock.changePercent > 0 ? <TrendingUp className="w-2 h-2" /> : stock.changePercent < 0 ? <TrendingDown className="w-2 h-2" /> : null}
                          {stock.changePercent === 0 ? '--' : `${Math.abs(stock.changePercent).toFixed(2)}%`}
                        </Badge>
                      </div>
                      <p className="text-[11px] text-muted-foreground truncate">{formatCompanyName(stock.name)}</p>
                    </div>
                    <div className="flex items-center gap-2 ml-2">
                      <div className="text-right">
                        <p className="font-medium text-[13px] text-card-foreground">{stock.price ? stock.price.toLocaleString() : '--'}</p>
                        <p className={`text-[11px] ${changeClass}`}>{stock.change === 0 ? '--' : `${stock.change > 0 ? '+' : ''}${stock.change.toFixed(0)}`}</p>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleFavorite(stock.symbol); }}
                        className="hover:scale-110 transition-transform p-1 flex-shrink-0"
                        title={isFav ? `Remove from ${activeList.name}` : favorites.length >= MAX_FAVORITES ? `${activeList.name} is full (max ${MAX_FAVORITES})` : `Add to ${activeList.name}`}
                      >
                        {isFav ? (
                          <GreenPupilEye className="w-4 h-4 text-muted-foreground" isActive={isFav} />
                        ) : (
                          <CirclePlus className={`w-4 h-4 ${favorites.length >= MAX_FAVORITES ? 'text-muted-foreground/40' : 'text-muted-foreground hover:text-foreground'}`} />
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </CardHeader>

      <CardContent className="flex-1 overflow-y-auto p-1.5 pt-0.5 px-[5%]">
        <div className="space-y-0.5">
          {isLoading ? (
            <div className="space-y-1.5 pt-1">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 px-2 rounded border border-border animate-pulse">
                  <div className="flex-1 min-w-0 space-y-2 py-1">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-slate-700/20 dark:bg-slate-700/50"></div>
                      <div className="h-3 w-12 bg-slate-700/20 dark:bg-slate-700/50 rounded"></div>
                      <div className="h-3 w-8 bg-slate-700/20 dark:bg-slate-700/50 rounded"></div>
                    </div>
                    <div className="h-2 w-24 bg-slate-700/20 dark:bg-slate-700/50 rounded"></div>
                  </div>
                  <div className="text-right space-y-2">
                    <div className="h-3 w-10 bg-slate-700/20 dark:bg-slate-700/50 rounded ml-auto"></div>
                    <div className="h-2 w-6 bg-slate-700/20 dark:bg-slate-700/50 rounded ml-auto"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-8 text-muted-foreground">
              <p className="text-red-600 mb-2">{error}</p>
            </div>
          ) : favoriteStocks.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <GreenPupilEye className="w-8 h-8 mx-auto mb-2 text-muted-foreground/30" isActive={false} />
              <p className="text-sm font-medium">No stocks in {activeList.name} yet</p>
              <p className="text-xs mt-1">Search stocks above and click the + icon to add</p>
            </div>
          ) : (
            favoriteStocks.map(stock => {
              const changeClass = stock.change === 0 ? 'text-muted-foreground' : stock.change > 0 ? 'text-green-600' : 'text-red-600';
              const badgeVariant = stock.changePercent === 0 ? 'secondary' : stock.changePercent > 0 ? 'default' : 'destructive';
              return (
                <div
                  key={stock.symbol}
                  className={`flex items-center justify-between py-1.5 px-2 rounded border cursor-pointer transition-all duration-200 hover:shadow-sm
                    ${selectedStock === stock.symbol ? 'border-primary bg-primary/5 shadow-sm' : 'border-border hover:border-primary/50'}`}
                  onClick={() => onStockSelect(stock.symbol)}
                  onContextMenu={(e) => handleContextMenu(e, stock.symbol)}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <button
                        onClick={(e) => toggleFavorite(stock.symbol, e)}
                        className="hover:scale-110 transition-transform flex-shrink-0"
                        title="Remove from watchlist"
                      >
                        <GreenPupilEye className="w-3 h-3 text-muted-foreground" />
                      </button>
                      <span className={`font-semibold text-[13px] ${selectedStock === stock.symbol ? 'text-primary' : 'text-card-foreground'}`}>
                        {stock.symbol}
                      </span>
                      <Badge variant={badgeVariant} className="text-[9px] px-1 py-0 h-3.5 flex items-center gap-0.5 ml-0.5">
                        {stock.changePercent > 0 ? <TrendingUp className="w-2 h-2" /> : stock.changePercent < 0 ? <TrendingDown className="w-2 h-2" /> : null}
                        {Number.isFinite(stock.changePercent) ? `${Math.abs(stock.changePercent).toFixed(2)}%` : '--'}
                      </Badge>
                    </div>
                    <p className="text-[11px] text-muted-foreground truncate pr-2">{formatCompanyName(stock.name)}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className={`font-semibold text-[13px] ${selectedStock === stock.symbol ? 'text-primary' : 'text-card-foreground'}`}>
                      {Number.isFinite(stock.price) ? stock.price.toLocaleString() : '--'}
                    </p>
                    <p className={`text-[11px] ${changeClass}`}>
                      {Number.isFinite(stock.change) ? `${stock.change > 0 ? '+' : ''}${stock.change.toFixed(0)}` : '--'}
                    </p>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </CardContent>
      {contextMenu && (
        <div
          className="fixed z-[100] bg-popover text-popover-foreground rounded-md shadow-md border border-border py-1 text-sm min-w-[150px]"
          style={{ top: contextMenu.y, left: contextMenu.x }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="w-full text-left px-3 py-1.5 hover:bg-accent hover:text-accent-foreground text-destructive flex items-center gap-2"
            onClick={() => {
              removeFavorite(contextMenu.symbol);
            }}
          >
            <GreenPupilEye className="w-4 h-4" isActive={false} />
            Remove from {activeList.name}
          </button>
          {otherLists.length > 0 && (
            <>
              <div className="my-1 border-t border-border" />
              <div className="px-3 py-1 text-[11px] text-muted-foreground">Copy to</div>
              {otherLists.map(list => {
                const alreadyAdded = list.symbols.includes(contextMenu.symbol);
                const isFull = list.symbols.length >= MAX_FAVORITES;
                return (
                  <button
                    key={list.id}
                    disabled={alreadyAdded || isFull}
                    className="w-full text-left px-3 py-1.5 hover:bg-accent hover:text-accent-foreground disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-popover-foreground flex items-center justify-between gap-3"
                    onClick={() => copyToList(contextMenu.symbol, list.id)}
                  >
                    <span className="truncate">{list.name}</span>
                    <span className="text-[11px] text-muted-foreground shrink-0">
                      {alreadyAdded ? 'Added' : isFull ? 'Full' : `${list.symbols.length}/${MAX_FAVORITES}`}
                    </span>
                  </button>
                );
              })}
            </>
          )}
        </div>
      )}
      {listMenuTarget && listMenu && (
        <div
          className="fixed z-[100] bg-popover text-popover-foreground rounded-md shadow-md border border-border py-1 text-sm min-w-[170px]"
          style={{ top: listMenu.y, left: listMenu.x }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="w-full text-left px-3 py-1.5 hover:bg-accent hover:text-accent-foreground flex items-center gap-2"
            onClick={() => startRenameList(listMenuTarget)}
          >
            <Pencil className="w-4 h-4" />
            Rename
          </button>
          {groups.lists.length > 1 && (
            <button
              className="w-full text-left px-3 py-1.5 hover:bg-accent hover:text-accent-foreground text-destructive flex items-center gap-2"
              onClick={() => (confirmDeleteId === listMenuTarget.id ? deleteList(listMenuTarget.id) : setConfirmDeleteId(listMenuTarget.id))}
            >
              <Trash2 className="w-4 h-4" />
              {confirmDeleteId === listMenuTarget.id ? 'Click again to delete' : 'Delete watchlist'}
            </button>
          )}
        </div>
      )}
    </Card>
  );
}
